import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  ArrowRight,
  Camera,
  Utensils,
  Music,
  Sparkles,
  Users,
  Palette,
  Flower,
  Star,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { getFieldsWithServiceCount } from "redux/features/service/serviceSlice";
import { EASING } from "../../utils/easingUtils";

interface ModernServicesSectionProps {
  className?: string;
  services?: any[];
  feature?: string;
}

interface ServiceCategory {
  id: string;
  name: string;
  icon: any;
  color: string;
  count: number;
}

const ModernServicesSection: React.FC<ModernServicesSectionProps> = ({
  className = "",
  services = [],
  feature = "/prestations?field=",
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const dispatch = useAppDispatch();
  const serviceState = useAppSelector((state) => state.service);
  const fieldsWithServiceCount = serviceState.fieldsWithServiceCount || [];

  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  // Fetch fields with service count on component mount
  useEffect(() => {
    dispatch(getFieldsWithServiceCount());
  }, [dispatch]);

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setItemsPerView(4);
      else if (window.innerWidth >= 1024) setItemsPerView(3);
      else if (window.innerWidth >= 768) setItemsPerView(2);
      else setItemsPerView(1);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset slide when category changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedCategory]);

  // Function to get icon based on field name
  const getIconForField = (fieldName: string) => {
    const lowerName = fieldName.toLowerCase();
    if (lowerName.includes("traiteur") || lowerName.includes("restaur") || lowerName.includes("cuisine"))
      return Utensils;
    if (lowerName.includes("photo")) return Camera;
    if (lowerName.includes("music") || lowerName.includes("dj") || lowerName.includes("son")) return Music;
    if (lowerName.includes("décor") || lowerName.includes("design")) return Palette;
    if (lowerName.includes("fleur") || lowerName.includes("bouquet")) return Flower;
    return Users; // default icon
  };

  // Function to get color based on field name
  const getColorForField = (fieldName: string) => {
    const lowerName = fieldName.toLowerCase();
    if (lowerName.includes("traiteur") || lowerName.includes("restaur") || lowerName.includes("cuisine"))
      return "from-[#f7e67c] to-[#f79dcb]";
    if (lowerName.includes("photo")) return "from-[#4663ac] to-[#c1d8f0]";
    if (lowerName.includes("music") || lowerName.includes("dj") || lowerName.includes("son"))
      return "from-[#c1d8f0] to-[#f7e67c]";
    if (lowerName.includes("décor") || lowerName.includes("design")) return "from-[#f79dcb] to-[#4663ac]";
    if (lowerName.includes("fleur") || lowerName.includes("bouquet")) return "from-[#f7e67c] to-[#c1d8f0]";
    return "from-gray-500 to-gray-600"; // default color
  };

  // Dynamic categories from database - only show fields with services
  const categories: ServiceCategory[] = [
    {
      id: "all",
      name: "Tous",
      icon: Sparkles,
      color: "from-[#4663ac] to-[#c1d8f0]",
      count: Array.isArray(services) ? services.length : 0,
    },
    ...fieldsWithServiceCount
      .filter((field) => field.serviceCount > 0) // Only show categories with services
      .map((field) => ({
        id: field.id.toString(),
        name: field.designation,
        icon: getIconForField(field.designation),
        color: getColorForField(field.designation),
        count: field.serviceCount,
      })),
  ];

  // Filtrer les services selon la catégorie sélectionnée
  const filteredServices = Array.isArray(services)
    ? services.filter((service: any) => {
        if (selectedCategory === "all") return true;

        // Find the selected field by ID
        const selectedField = fieldsWithServiceCount.find((f) => f.id.toString() === selectedCategory);
        if (!selectedField) return false;

        // Check if service field matches selected category
        const serviceField = service.ServiceAgent?.fields?.toLowerCase() || "";
        const fieldName = selectedField.designation?.toLowerCase() || "";

        // Priorité 1: ServiceAgent.fields (champ officiel du prestataire)
        if (serviceField) {
          return serviceField.includes(fieldName);
        }

        // Priorité 2: Si pas de champ ServiceAgent, utiliser le titre du service
        const serviceTitle = service.title?.toLowerCase() || "";
        return serviceTitle.includes(fieldName);
      })
    : [];

  const servicesToShow = filteredServices;

  // Slider navigation
  const totalSlides = Math.ceil(servicesToShow.length / itemsPerView);
  const canGoNext = currentSlide < totalSlides - 1;
  const canGoPrev = currentSlide > 0;

  const nextSlide = () => {
    if (canGoNext) setCurrentSlide((prev) => prev + 1);
  };

  const prevSlide = () => {
    if (canGoPrev) setCurrentSlide((prev) => prev - 1);
  };

  const goToSlide = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: EASING.easeOut,
      },
    },
  };

  return (
    <div
      className={`py-24 bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-800/50 ${className}`}
      ref={ref}
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 mb-6">
            <Users className="w-6 h-6 text-[#4663ac]" />
            <span className="text-blue-500 font-semibold uppercase tracking-wide text-sm">
              Prestataires Premium
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Trouvez les meilleurs{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              prestataires
            </span>
            <br />
            près de chez vous
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed">
            Complétez votre événement avec les meilleurs professionnels. Traiteurs, photographes, décorateurs,
            animateurs, DJ... Profitez d'un service clé en main pour des événements inoubliables.
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-full transition-all duration-300 ${
                  selectedCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{category.name}</span>
                {category.count > 0 && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === category.id
                        ? "bg-white/20 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {category.count}
                  </span>
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Services Slider */}
        <div className="relative mb-12">
          {/* Slider Container */}
          <div className="overflow-hidden">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="flex transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)`,
                width: `${(servicesToShow.length / itemsPerView) * 100}%`,
              }}
            >
              {servicesToShow.map((service: any, index: number) => (
                <motion.div
                  key={service.id}
                  variants={cardVariants}
                  whileHover={{
                    scale: 1.03,
                    y: -10,
                  }}
                  className="group relative flex-shrink-0 px-3"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <Link to={`/service/${service.id}`}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 h-80 flex flex-col">
                      {/* Image Container */}
                      <div className="relative h-48 overflow-hidden flex-shrink-0">
                        {service.images && service.images.length > 0 ? (
                          <img
                            src={process.env.REACT_APP_CLOUD_FRONT + service.images[0]}
                            alt={service.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div
                            className={`w-full h-full bg-gradient-to-br ${(() => {
                              const field = service.ServiceAgent?.fields?.toLowerCase() || "";
                              const category = categories.find((c) => {
                                return field ? field.includes(c.id) : service.title?.toLowerCase().includes(c.id);
                              });
                              return category?.color || "from-gray-400 to-gray-500";
                            })()} flex items-center justify-center`}
                          >
                            {(() => {
                              const field = service.ServiceAgent?.fields?.toLowerCase() || "";
                              const category = categories.find((c) => {
                                return field ? field.includes(c.id) : service.title?.toLowerCase().includes(c.id);
                              });
                              const Icon = category?.icon || Users;
                              return <Icon className="w-16 h-16 text-white/70" />;
                            })()}
                          </div>
                        )}

                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Rating Badge */}
                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          <span className="text-xs font-semibold text-gray-800">4.7</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex-1 flex flex-col justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 transition-colors">
                            {service.title}
                          </h3>

                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {service.description?.slice(0, 80)}...
                          </p>

                          {/* Location */}
                          <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 mb-4">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm">Île-de-France</span>
                          </div>
                        </div>

                        {/* Action */}
                        <div className="flex items-center justify-between mt-auto">
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
                            Voir les offres
                          </span>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          {servicesToShow.length > itemsPerView && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevSlide}
                disabled={!canGoPrev}
                className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  canGoPrev
                    ? "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
              >
                <ChevronLeft className="w-6 h-6" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextSlide}
                disabled={!canGoNext}
                className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  canGoNext
                    ? "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </>
          )}

          {/* Pagination Indicators */}
          {servicesToShow.length > itemsPerView && totalSlides > 1 && (
            <div className="flex justify-center space-x-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index
                      ? "bg-blue-600 scale-110"
                      : "bg-gray-300 dark:bg-gray-600 hover:bg-blue-400 dark:hover:bg-blue-500"
                  }`}
                  aria-label={`Aller à la diapositive ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* No Services Found */}
        {filteredServices.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Aucun prestataire trouvé
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              Essayez de modifier vos critères de recherche ou sélectionnez une autre catégorie
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ModernServicesSection;
